import { NhostClientOptions } from '@nhost/nhost-js'
import { DEFAULT_NOTIFICATIONS_DEFAULTS_SINCE } from '@rolebase/shared/model/notifications'

export const isLocal = location.hostname === 'localhost'
export const isStaging = location.hostname === 'staging--rolebase.netlify.app'
// ⚠️ Same instance table as the `map $host` blocks in packages/webapp/nginx.conf,
// which picks the brand BEFORE JavaScript runs (title, link previews, icons,
// manifest — #105). An instance added here must be added there too.
export const isYocoop = location.hostname === 'gouv.yocoop.fr'
export const isUdN = location.hostname === 'gouv.universite-du-nous.org'
export const isShareApp = /^\/share(\/|$)/.test(location.pathname)

const nhostCommon = {
  autoSignIn: !isShareApp,
  autoRefreshToken: !isShareApp,
  clientStorageType: isShareApp ? 'cookie' : ('localStorage' as const),
}

export default {
  url: isLocal
    ? 'http://localhost:5175'
    : isStaging
    ? 'https://staging--rolebase.netlify.app'
    : isYocoop
    ? 'https://gouv.yocoop.fr'
    : isUdN
    ? 'https://gouv.universite-du-nous.org'
    : 'https://rolebase.io',

  // "Book a demo", offered from the onboarding todo. An instance value (#94,
  // Q13): the UdN books through its own calendar; GouvCoop points to Yohan's
  // email for testing. Empty hides the link.
  demoUrl: isUdN
    ? 'https://rdv.universite-du-nous.org/u/yohan-reversat/cartogroov-rdv-1h'
    : isYocoop
    ? 'mailto:yohan.reversat@lilo.org'
    : 'https://cal.com/lonestone-godefroy/rolebase-demo',

  // « Écrire à l'équipe » in « À propos » (#77): who to write to about this
  // instance. An instance value, like demoUrl; the address is never shown, the
  // button opens a mail to it. Empty hides the button.
  contactEmail: isUdN
    ? 'yohan.reversat@universite-du-nous.org'
    : isYocoop
    ? 'yohan.reversat@lilo.org'
    : '',

  // Audience measurement (#94, Q15-Q17). Only the UdN measures, on its own
  // central Umami (analyse.universite-du-nous.org, 30/09/2026; it replaced the
  // one on the CartoGroov VPS). Its tracker is renamed lumiere.js and already
  // knows its collect endpoint (/api/relais). null = no script at all:
  // GouvCoop, the developer machine and anything else.
  analytics: isUdN
    ? {
        scriptUrl: 'https://analyse.universite-du-nous.org/lumiere.js',
        websiteId: 'ab32d2ea-e014-445a-8dce-258b7c9c3ca9',
        domain: 'gouv.universite-du-nous.org',
        // Heatmaps only: replays are off on the Umami server (03/10/2026)
        recorderUrl: 'https://analyse.universite-du-nous.org/recorder.js',
      }
    : null,

  // The two AI buttons inherited from Godefroy (« Remplir automatiquement » on
  // an empty role, « Générer automatiquement » on a meeting summary) call
  // OpenAI through the backend. No AI is plugged in on our instances, so they
  // only returned an error — and plugged in, they would send meeting contents
  // to the United States. Hidden, not removed (#140, 28/09/2026): the day an
  // AI is chosen (#141), this value shows them again.
  aiEnabled: !(isYocoop || isUdN),

  // Where "Help > Documentation" leads. Rolebase's docs describe Godefroy's
  // product, not our instances: empty here hides the entry (#94, 24/09/2026).
  docsUrl: isYocoop || isUdN ? '' : 'https://rolebase.io',

  websiteUrl: isYocoop
    ? 'https://yocoop.fr'
    : isUdN
    ? 'https://universite-du-nous.org'
    : 'https://rolebase.io',

  nhost: (isLocal
    ? { subdomain: 'local', ...nhostCommon }
    : isStaging
    ? {
        subdomain: 'jjvdhpoooerochuiusam',
        region: 'eu-central-1',
        ...nhostCommon,
      }
    : isYocoop
    ? {
        // Every URL the SDK builds itself ends with /v1 (cloud and `nhost dev`
        // alike), and its anti-recursion guard tests url.endsWith("/v1/token")
        // literally. Overriding authUrl without /v1 made that guard miss, so
        // refreshSession() re-entered itself from inside its own exclusive
        // nhostSessionLock — which isn't reentrant — and froze the app.
        // Auth serves its routes under /v1 (AUTH_API_PREFIX, deploy/compose.yml).
        authUrl: 'https://auth.gouv.yocoop.fr/v1',
        storageUrl: 'https://storage.gouv.yocoop.fr/v1',
        graphqlUrl: 'https://hasura.gouv.yocoop.fr/v1/graphql',
        ...nhostCommon,
      }
    : isUdN
    ? {
        // Mêmes sous-domaines que ceux servis aujourd'hui par CartoGroov, et
        // le même suffixe /v1 que Yocoop, pour la raison expliquée plus haut.
        authUrl: 'https://auth.gouv.universite-du-nous.org/v1',
        storageUrl: 'https://storage.gouv.universite-du-nous.org/v1',
        graphqlUrl: 'https://hasura.gouv.universite-du-nous.org/v1/graphql',
        ...nhostCommon,
      }
    : {
        subdomain: 'fsudktxishllphxeibqs',
        region: 'eu-central-1',
        ...nhostCommon,
      }) as NhostClientOptions,

  functionsUrl: isLocal
    ? 'https://local.functions.nhost.run/v1/'
    : isStaging
    ? 'https://jjvdhpoooerochuiusam.functions.eu-central-1.nhost.run/v1/'
    : isYocoop
    ? 'https://api.gouv.yocoop.fr/v1/'
    : isUdN
    ? // Aligné sur le backend, comme chez Yocoop. CartoGroov pointait vers
      // `functions.gouv.universite-du-nous.org`, un service mort avant comme
      // après la bascule (voir docs/bascule-udn-reprise.md § 3.5).
      'https://api.gouv.universite-du-nous.org/v1/'
    : 'https://fsudktxishllphxeibqs.functions.eu-central-1.nhost.run/v1/',

  backendUrl: isLocal
    ? 'http://localhost:8888'
    : isYocoop
    ? 'https://api.gouv.yocoop.fr'
    : isUdN
    ? 'https://api.gouv.universite-du-nous.org'
    : 'https://api.rolebase.io',

  yjsCollab: {
    url: isLocal
      ? 'ws://localhost:1234'
      : isYocoop
      ? 'wss://collab.gouv.yocoop.fr'
      : isUdN
      ? // Un chemin, pas un sous-domaine : la route existe chez eux
        // (`PathPrefix(/yjs)`, prioritaire sur la webapp), elle a été
        // reconstruite telle quelle au passage à Traefik le 17/08 et vérifiée
        // depuis l'extérieur. Un sous-domaine coûterait DNS + certificat +
        // route pour rien.
        'wss://gouv.universite-du-nous.org/yjs'
      : 'wss://collab.rolebase.io',
  },

  notifications: {
    // Pivot date of the notification defaults (EM-D3 / EM-D8).
    // 🔑 Not a hostname branch, unlike everything else in this file: this value
    // must equal the backend's to the day, so it comes from the SAME .env entry,
    // injected at build time (docker-compose passes NOTIFICATIONS_DEFAULTS_SINCE
    // as the VITE_ build arg), with the same shared constant as fallback. Two
    // literals living side by side had already drifted apart outside Yocoop.
    defaultsSince:
      import.meta.env.VITE_NOTIFICATIONS_DEFAULTS_SINCE ||
      DEFAULT_NOTIFICATIONS_DEFAULTS_SINCE,
  },

  // The delivered version (docs/versions/README.md), baked in at build time from
  // `git describe --tags` (docker-compose passes APP_VERSION as the VITE_ build
  // arg). The git tag is its only source: package.json stays at 1.0.0 on
  // purpose. Absent → null, shown as "unknown version", never a made-up number.
  version: import.meta.env.VITE_APP_VERSION || null,

  // The free software this instance derives from (MIT, © 2022 Rolebase),
  // credited in a visible place (#83): bottom of the sidebar and « À propos ».
  // Empty on rolebase.io, which IS the product.
  sourceUrl: isYocoop || isUdN ? 'https://github.com/Godefroy/rolebase' : '',

  brand: {
    // The product logo, per instance. Served from public/ rather than imported:
    // bundling it would ship BOTH logos to every visitor, including the one
    // they will never see.
    //
    // ⚠️ Known and accepted: brand-gouvcoop.png carries the YoCoop logo, while
    // the product will be named GouvCoop. Yohan has no GouvCoop logo yet
    // (15/08/2026) — the day he does, only the file changes, not this code.
    //
    // ✅ La branche isUdN a été posée le 17/08/2026, avec toutes les autres
    // valeurs UdN d'un seul geste.
    logoUrl: isYocoop
      ? '/brand-gouvcoop.png'
      : isUdN
      ? '/brand-cartogroov.png'
      : undefined,

    // The product name shown to users. Yocoop sells GouvCoop, the UdN sells
    // CartoGroov, and neither audience has ever heard of Rolebase — nor needs
    // to. One value interpolated into the texts, never fourteen duplicated
    // strings (UDN03b-D3).
    name: isYocoop ? 'GouvCoop' : isUdN ? 'CartoGroov' : 'Rolebase',
  },

  // Legal pages, per instance. They are shown BEFORE anyone is signed in, so
  // they cannot depend on an organization — this is instance-level by nature
  // (UDN03b-D4), and that is what made NEW-03 impossible to scope on its own.
  //
  // Empty everywhere today, on purpose: Yohan has no legal pages for GouvCoop
  // yet (15/08/2026). When all three are empty, the notice AND the signup
  // checkbox disappear — making someone tick a box for conditions they cannot
  // read commits nothing and muddles the message (D5, option a).
  //
  // ✅ Les trois pages de l'UdN existent déjà, hébergées sur leur site : rien à
  // écrire, seulement à pointer (posé le 17/08/2026).
  legal: isUdN
    ? {
        terms: 'https://universite-du-nous.org/cartogroov-cgu',
        sales: 'https://universite-du-nous.org/cartogroov-cgv',
        privacy: 'https://universite-du-nous.org/cartogroov-policy',
      }
    : {
        terms: '',
        sales: '',
        privacy: '',
      },

  errorReporting: {
    // Where browser errors are also sent, on top of our own server. Empty
    // everywhere here, and that is the point: Yocoop keeps the minimal book
    // (server journal only), so no external destination and no SDK loaded at
    // all — see ENR09-D1 and D18.
    //
    // 🔑 Yocoop reste vide (carnet minimal, journal serveur seulement). Côté
    // UdN, l'ancien DSN pointait vers `sentry.lonestone.io`, un serveur tiers
    // (retiré par NEW-06 pour raison RGPD sur un outil qui porte des tensions
    // privées). Le GlitchTip d'ENR-09 le remplace : auto-hébergé sur NOTRE
    // infra (`log.gouv.universite-du-nous.org`), erreurs uniquement, aucun
    // session replay (buildSdkOptions force integrations: []). Activé le
    // 31/08/2026, projet « Cartogroov ». La DSN n'est pas un secret (elle vit
    // dans le JS client), d'où le branchement par hôte comme le reste du fichier.
    dsn: isUdN
      ? 'https://fe717cd48c2d4beaac8106542c058f22@log.gouv.universite-du-nous.org/1'
      : '',
  },

  memberPicture: {
    maxSize: 300,
  },
  orgIcon: {
    maxSize: 256, // in px
  },

  stripe: {
    publicKey:
      isLocal || isStaging
        ? 'pk_test_51MTnUCFbDx5R7pIdjvJ0kQ6gzkXExNcMJxSmAhc6tmF2dTu3qYa4tNQZBFqcy3ZNCobM9cxq4w9nn3gnpHKddHDn00vrm59S4L'
        : 'pk_live_51MTnUCFbDx5R7pIdX4eYwBp6vgGF6oc47ipEL94az2BRzxgXa8p738VMYfmf2824MNiZuuqbsgtwDcaS755gcUzU00ZeBi5QjW',
  },

  crisp: {
    websiteId: isYocoop
      ? '30613203-bbd5-4397-8e4a-3b2515214ba6'
      : isUdN
      ? '1b725524-ddc9-48ee-8ef6-2dfcfe831eb9'
      : '652544cd-14f6-4c8c-9a04-2a56676dd4a0',
  },

  apps: {
    office365: {
      clientId: 'b92369ce-3fc5-4873-987a-335f0917672b',
    },
    googlecalendar: {
      // 🔑 Chaque instance a son propre client OAuth : les URI de redirection
      // y sont déclarées par domaine. Servir celui de Godefroy à CartoGroov
      // ferait échouer la connexion à l'agenda, sans que rien d'autre bouge.
      clientId: isYocoop
        ? '804770565715-e37h6m717d8snp3suuojg76oa4bjsih2.apps.googleusercontent.com'
        : isUdN
        ? '387279297098-e4v4jm8mtf4lr5leoq1ovuiqvh28gv3v.apps.googleusercontent.com'
        : '749917420406-vpf503tb2pnvvcm6v16jh6oe1js7n155.apps.googleusercontent.com',
    },
  },
}
